import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { auth } from '../lib/supabase'
import { useRecaptcha } from '../hooks/useRecaptcha'
import Header from '../components/Header'

export default function Login() {
  const navigate = useNavigate()
  const { verifyRecaptcha } = useRecaptcha()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Verify reCAPTCHA
    const recaptchaResult = await verifyRecaptcha('login')
    if (!recaptchaResult.success) {
      setError('Error de verificación. Por favor intenta de nuevo.')
      setLoading(false)
      return
    }

    const { error: signInError } = await auth.signIn(email, password)

    if (signInError) {
      setError('Credenciales inválidas')
    } else {
      navigate('/admin')
    }

    setLoading(false)
  }

  return (
    <>
      <Helmet>
        <title>Login - Plaza Aurora Admin</title>
      </Helmet>

      <Header />

      <div style={{
        minHeight: 'calc(100vh - 68px)',
        marginTop: '68px',
        background: 'var(--background)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-card)',
          border: 'var(--card-border)',
          padding: '2.5rem',
          width: '100%',
          maxWidth: '440px',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <h1 style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 'var(--displayXL)',
              fontWeight: 'var(--bold)',
              color: 'var(--textPrimary)',
              margin: '0 0 0.5rem 0',
              lineHeight: '1.1'
            }}>
              Plaza Aurora
            </h1>
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'var(--body)',
              color: 'var(--textMuted)',
              margin: 0
            }}>
              Panel de Administración
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            {/* Email Input */}
            <div>
              <label style={{
                display: 'block',
                fontFamily: "'Inter', sans-serif",
                fontSize: 'var(--small)',
                fontWeight: 'var(--medium)',
                color: 'var(--textPrimary)',
                marginBottom: '0.5rem'
              }}>
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '1px solid var(--mutedStroke)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'var(--body)',
                  color: 'var(--textPrimary)',
                  background: 'var(--surface)',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = 'none'
                  e.currentTarget.style.borderColor = 'var(--accentPurple)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(184, 146, 209, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Password Input */}
            <div>
              <label style={{
                display: 'block',
                fontFamily: "'Inter', sans-serif",
                fontSize: 'var(--small)',
                fontWeight: 'var(--medium)',
                color: 'var(--textPrimary)',
                marginBottom: '0.5rem'
              }}>
                Contraseña
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '1px solid var(--mutedStroke)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'var(--body)',
                  color: 'var(--textPrimary)',
                  background: 'var(--surface)',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = 'none'
                  e.currentTarget.style.borderColor = 'var(--accentPurple)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(184, 146, 209, 0.1)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mutedStroke)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                background: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
                color: '#DC2626',
                padding: '0.875rem 1rem',
                borderRadius: 'var(--radius-md)',
                fontFamily: "'Inter', sans-serif",
                fontSize: 'var(--small)',
                border: '1px solid #FCA5A5'
              }}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: loading ? 'var(--textMuted)' : 'var(--ctaBg)',
                color: 'var(--ctaText)',
                borderRadius: 'var(--radius-md)',
                fontFamily: "'Inter', sans-serif",
                fontSize: 'var(--body)',
                fontWeight: 'var(--bold)',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: 'var(--shadow-soft)',
                border: 'var(--card-border)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                  e.currentTarget.style.background = 'var(--brand-blue)'
                  e.currentTarget.style.color = 'white'
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'var(--shadow-soft)'
                  e.currentTarget.style.background = 'var(--ctaBg)'
                  e.currentTarget.style.color = 'var(--ctaText)'
                }
              }}
              onMouseDown={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-400)'
                  e.currentTarget.style.background = 'var(--brand-blue-600)'
                }
              }}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>
        </div>
      </div>
    </>
  )
}

